import {Show} from 'platform/foundation';

import {isTruthy} from 'ramda-adjunct';

import {
  GROUP_HEADER_CELL_BORDER_BOTTOM,
  PINNED_RIGHT_EDGE_LINE_STYLE,
} from '../../../../constants/borderStyles';
import {SELECTION_COLUMN_WIDTH} from '../../../../constants/selectionColumn';
import type {GroupRowModel} from '../../../../utils/groupRowModel';
import {GroupHeaderCell} from './components/GroupHeaderCell/GroupHeaderCell';

export type GroupHeaderRowProps = {
  /** Live-order model built by useGroupRowModel (EagGridTable). */
  model: GroupRowModel;
  hasCheckboxColumn?: boolean;
  // When action column is enabled, add a matching empty <th> for structure
  actionColumnWidth?: number;
};

/**
 * Renders the group header <tr> that sits above the column header row, from
 * the LIVE rendered column order: each consecutive run of same-group columns
 * becomes a <th colSpan={n}> (draggable — moving it reorders the whole block),
 * ungrouped columns get an empty placeholder <th colSpan={1}> (drop target).
 *
 * The same groupId may produce multiple cells when its members are
 * non-consecutive in the live order; each run has a unique id (occurrence
 * suffix) to keep React keys and drag ids unambiguous.
 */
export function GroupHeaderRow(props: GroupHeaderRowProps) {
  const beforeFiller = props.model.cells.slice(0, props.model.fillerIndex);
  const afterFiller = props.model.cells.slice(props.model.fillerIndex);

  return (
    <tr>
      {/* Match the leading selection column so group spans align with the data columns. */}
      <Show when={props.hasCheckboxColumn}>
        <th
          aria-hidden
          className="p-0 bg-white"
          style={{
            width: SELECTION_COLUMN_WIDTH,
            position: 'sticky',
            top: 0,
            left: 0,
            zIndex: 'var(--z-content-loader)',
            ...GROUP_HEADER_CELL_BORDER_BOTTOM,
          }}
        />
      </Show>
      {beforeFiller.map((cell, index, arr) => (
        <GroupHeaderCell key={cell.id} cell={cell} isLast={index === arr.length - 1} />
      ))}
      {/* filler th: pairs with filler <col /> between unpinned and right-pinned columns */}
      <th
        aria-hidden
        style={{
          position: 'sticky',
          top: 0,
          background: 'white',
          ...GROUP_HEADER_CELL_BORDER_BOTTOM,
        }}
      />
      {afterFiller.map((cell, index, arr) => (
        <GroupHeaderCell key={cell.id} cell={cell} isLast={index === arr.length - 1} />
      ))}
      {/* empty action column placeholder — maintains structure alignment with the action header cell */}
      <Show when={isTruthy(props.actionColumnWidth)}>
        <th
          aria-hidden
          className="p-0 border-0 bg-gray-50"
          style={{
            position: 'sticky',
            top: 0,
            right: 0,
            zIndex: 'var(--z-content-loader)',
            width: props.actionColumnWidth,
            maxWidth: props.actionColumnWidth,
            ...GROUP_HEADER_CELL_BORDER_BOTTOM,
          }}
        >
          <span style={PINNED_RIGHT_EDGE_LINE_STYLE} />
        </th>
      </Show>
    </tr>
  );
}
